import { Listbox, Transition } from "@headlessui/react";
import cx from "classnames";
import { forwardRef, Fragment, ReactElement, Ref } from "react";

import { Checkmark } from "@/components/icons/checkmark";
import { ExpandCollapseCaret } from "@/components/icons/expand-collapse-caret";

import { FormControl } from "./form-control";

type SelectProps<T = string> = {
  placeholder?: string;
  value: T;
  options: { label: string | React.ReactNode; value: T }[];
  onChange: (value: T) => void;
  name: string;
  label: string;
  selectedLabel?: string | React.ReactNode;
  disabled?: boolean;
  className?: string;
  hasError?: boolean;
  hideLabel?: boolean;
  dataTestId?: string;
};

const SelectComponent = <T extends string>(
  {
    placeholder = "",
    value,
    options,
    onChange,
    name,
    disabled,
    className,
    hasError,
    dataTestId,
    label,
    selectedLabel,
  }: SelectProps<T>,
  ref: Ref<HTMLDivElement>
) => {
  return (
    <Listbox
      ref={ref}
      value={value}
      onChange={onChange}
      name={name}
      disabled={disabled}
    >
      {({ open }) => (
        <FormControl label={label} id={name} className={className}>
          <div className="relative">
            <Listbox.Button
              data-testid={dataTestId ?? ""}
              id={name}
              className={cx(
                "w-full truncate relative focus-visible:ring-primary cursor-pointer rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-8 text-left text-sm focus:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:bg-gray-200 overflow-hidden",
                {
                  ["ring-primary ring-2"]: open,
                  "border-red-500": hasError,
                }
              )}
            >
              <span
                className={cx("block truncate", {
                  ["text-gray-600"]: !value,
                })}
              >
                {selectedLabel ||
                  options.find((opt) => opt.value === value)?.label ||
                  placeholder}
              </span>
              <ExpandCollapseCaret
                className={cx(
                  "absolute right-2 top-1/2 size-4 -translate-y-1/2 transition-all ease-in-out",
                  {
                    "rotate-180": open,
                  }
                )}
              />
            </Listbox.Button>
            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options
                className="absolute z-10 mt-1 max-h-60 w-full  overflow-auto rounded-lg  bg-white py-1 text-xs shadow-lg ring-1 ring-black/5 focus:outline-none"
                data-testid={dataTestId ? `${dataTestId}-options` : ""}
              >
                {options.map((opt, index) => (
                  <Listbox.Option
                    key={index}
                    className={({ active }) =>
                      `flex justify-between items-center cursor-default select-none py-2 px-3 text-sm ${
                        active ? "bg-gray-100 text-gray-900" : "text-gray-900"
                      }`
                    }
                    value={opt.value}
                  >
                    {({ selected }) => (
                      <>
                        <span
                          className={cx("block truncate pr-3", {
                            "font-medium": selected,
                            "font-normal": !selected,
                          })}
                        >
                          {opt.label}
                        </span>
                        <span className="flex w-6 items-center justify-center">
                          {selected ? (
                            <Checkmark className="size-4" aria-hidden="true" />
                          ) : (
                            <>&nbsp;</>
                          )}
                        </span>
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        </FormControl>
      )}
    </Listbox>
  );
};

export const Select = forwardRef(SelectComponent) as <T extends string>(
  p: SelectProps<T> & { ref?: Ref<HTMLDivElement> }
) => ReactElement;
